import type { WebsiteContent } from './types'

export const privacyPolicy: WebsiteContent['privacyPolicy'] = {
  title: 'Privacy Policy',
  effectiveDate: 'January 1, 2026',
  introduction: [
    'NXT Humans (“NXT Humans,” “we,” “us,” or “our”) values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, share, and protect your information when you visit our website, use our services, or otherwise interact with us.',
    'By accessing or using our services, you agree to the practices described in this policy.',
  ],
  sections: [
    {
      id: 'information-we-collect',
      heading: '1. Information We Collect',
      body:
        'We collect information to operate effectively and provide you with a better experience. This includes:',
      subSections: [
        {
          id: 'information-you-provide',
          heading: '1.1 Information You Provide',
          bullets: [
            {
              label: 'Contact Information',
              description: 'Name, email address, phone number, and mailing address',
            },
            {
              label: 'Professional Information',
              description: 'Job title, company name, and industry',
            },
            {
              label: 'Account Information',
              description: 'Username, password, and other credentials you create',
            },
          ],
        },
        {
          id: 'information-collected-automatically',
          heading: '1.2 Information Collected Automatically',
          bullets: [
            {
              label: 'Device & Technical Data',
              description:
                'IP address, browser type, device identifiers, operating system, time zone, and similar technical details',
            },
            {
              label: 'Usage Data',
              description: 'Pages visited, features used, interactions, and referring URLs',
            },
          ],
        },
        {
          id: 'cookies-and-tracking',
          heading: '1.3 Cookies & Tracking Technologies',
          body:
            'We use cookies and similar technologies to operate our website, analyze performance, and personalize your experience. You can manage cookie preferences through your browser settings. For more details, please see our Cookie Policy.',
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      heading: '2. How We Use Your Information',
      body: 'We use your information for legitimate business purposes, including to:',
      bullets: [
        'Provide, operate, and improve our services',
        'Respond to inquiries, requests, and support needs',
        'Personalize content and user experiences',
        'Send updates, newsletters, and marketing communications (you may opt out at any time)',
        'Analyze usage trends and improve performance',
        'Detect, prevent, and address fraud, security, or technical issues',
        'Comply with legal and regulatory obligations',
      ],
    },
    {
      id: 'legal-bases-for-processing',
      heading: '3. Legal Bases for Processing (Where Applicable)',
      body:
        'Where required by law (such as under GDPR), we process your personal data based on one or more of the following legal grounds:',
      bullets: [
        'Your consent',
        'Performance of a contract',
        'Legitimate business interests',
        'Compliance with legal obligations',
      ],
    },
    {
      id: 'how-we-share-your-information',
      heading: '4. How We Share Your Information',
      body:
        'We do not sell your personal information. We may share your information in limited circumstances, including:',
      bullets: [
        'Service Providers: Trusted third parties who help us operate our business (e.g., hosting, analytics, email delivery, customer support)',
        'Legal & Compliance: When required by law or in response to valid legal requests',
        'Business Transfers: In connection with a merger, acquisition, or sale of assets',
      ],
    },
    {
      id: 'data-security',
      heading: '5. Data Security',
      body:
        'We use reasonable administrative, technical, and organizational safeguards to protect your personal information. While we strive to use commercially acceptable means to protect data, no system is 100% secure, and we cannot guarantee absolute security.',
    },
    {
      id: 'data-retention',
      heading: '6. Data Retention',
      body:
        'We retain personal information only for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required or permitted by law.',
    },
    {
      id: 'your-privacy-rights',
      heading: '7. Your Privacy Rights',
      body: 'Depending on your location, you may have the right to:',
      bullets: [
        'Access the personal data we hold about you',
        'Correct inaccurate or incomplete information',
        'Request deletion of your personal data',
        'Object to or restrict certain processing',
        'Request data portability',
        'Withdraw consent at any time (where processing is based on consent)',
      ],
    },
    {
      id: 'marketing-communications',
      heading: '8. Marketing Communications',
      body:
        'You may opt out of marketing emails at any time by using the unsubscribe link in our emails or contacting us directly. Even if you opt out, we may still send non-promotional communications related to your account or services.',
    },
    {
      id: 'third-party-links',
      heading: '9. Third-Party Links',
      body:
        'Our website may include links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review their privacy policies before providing any information.',
    },
    {
      id: 'childrens-privacy',
      heading: "10. Children’s Privacy",
      body:
        'Our services are not intended for individuals under the age of 13, and we do not knowingly collect personal information from children.',
    },
    {
      id: 'changes-to-this-policy',
      heading: '11. Changes to This Policy',
      body:
        'We may update this Privacy Policy from time to time. When we do, we will update the effective date and post the revised policy on this page. We encourage you to review it periodically.',
    },
    {
      id: 'contact-us',
      heading: '12. Contact Us',
      body:
        'If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:',
    },
  ],
  contactEmail: 'info@nxthumans.com',
}
